import { RefreshCw } from 'lucide-react';
import { IconButton, SegmentedControl } from '@vnetwork/ui/primitives';

export interface DateRange {
  /** ISO start. */
  gte: string;
  /** ISO end. */
  lte: string;
}

export interface RangePreset {
  key: string;
  label: string;
  hours: number;
}

export const RANGE_PRESETS: RangePreset[] = [
  { key: '1h', label: '1h', hours: 1 },
  { key: '4h', label: '4h', hours: 4 },
  { key: '24h', label: '24h', hours: 24 },
  { key: '7d', label: '7d', hours: 168 },
];

/** Compute an ISO {gte,lte} window ending now for a preset key. */
export function rangeFor(presetKey: string): DateRange {
  const preset = RANGE_PRESETS.find((p) => p.key === presetKey) ?? RANGE_PRESETS[0];
  const now = new Date();
  const from = new Date(now.getTime() - preset.hours * 3600_000);
  return { gte: from.toISOString(), lte: now.toISOString() };
}

/**
 * Quick relative-range selector for monitoring/usage views. Token-styled segmented
 * buttons (replaces the legacy MUI X date-range picker). Emits the active preset
 * key; the caller derives the window with `rangeFor`.
 */
export function DateRangePicker({
  value,
  onChange,
  onRefresh,
}: {
  value: string;
  onChange: (presetKey: string) => void;
  onRefresh?: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
      <SegmentedControl
        size="sm"
        aria-label="Time range"
        value={value}
        onValueChange={onChange}
        items={RANGE_PRESETS.map((p) => ({ value: p.key, label: p.label }))}
      />
      {onRefresh && (
        <IconButton variant="ghost" size="sm" aria-label="Refresh" onClick={onRefresh}>
          <RefreshCw size={15} />
        </IconButton>
      )}
    </div>
  );
}
